import { IconBackArrowLeft, IconTrash } from '@holbonkit/icons'
import {
    BottomSheet,
    BottomSheetBody,
    BottomSheetClose,
    BottomSheetContent,
    BottomSheetHeader,
    BottomSheetTitle,
    Box,
    Button,
    Text,
} from '@holbonkit/ui'

import type { SelfEmployedPayoutMethod } from '@/shared/api'
import { getLocalStorageValue } from '@/shared/hooks/use-local-storage'
import { formatPhone } from '@/shared/lib/formatters'
import { BankIcon } from '@/shared/ui'

import { EMPTY_PAYOUT_METHOD } from '@/entities/smz'

import styles from './bottom-sheets.module.scss'

type TDeleteMethodProps = {
    handleDelete: () => void
    isDeleteMode: boolean
    setIsDeleteMode: () => void
}

export const DeleteBottomSheet = ({
    isDeleteMode,
    setIsDeleteMode,
    handleDelete,
}: TDeleteMethodProps) => {
    return (
        <BottomSheet open={isDeleteMode} onOpenChange={setIsDeleteMode}>
            <BottomSheetContent gripper showCloseButton={false}>
                <BottomSheetHeader className={styles['bottom-sheet__header']}>
                    <BottomSheetTitle>Удалить счёт?</BottomSheetTitle>
                    <BottomSheetClose>
                        <button
                            className={styles['bottom-sheet__header-back']}
                            data-test-id='sbp-bottom-sheet__back'
                            type='button'
                        >
                            <IconBackArrowLeft color='var(--text-and-icon-primary)' size={24} />
                        </button>
                    </BottomSheetClose>
                </BottomSheetHeader>
                <BottomSheetBody className={styles['bottom-sheet__content']}>
                    <Text variant='body-m-16-medium'>
                        Вывести средства без счёта СБП
                        <br />
                        не получится
                    </Text>
                    <Box className={styles['delete-method__buttons']}>
                        <Button
                            style={{ flex: 1 }}
                            variant='secondary'
                            onClick={() => setIsDeleteMode()}
                        >
                            Отмена
                        </Button>
                        <Button style={{ flex: 1 }} variant='error-high' onClick={handleDelete}>
                            Удалить
                        </Button>
                    </Box>
                </BottomSheetBody>
            </BottomSheetContent>
        </BottomSheet>
    )
}

type TEditMethodProps = {
    handleOpenDelete: () => void
    isEditMode: boolean
    setIsEditMode: () => void
}

export const EditBottomSheet = ({
    isEditMode,
    setIsEditMode,
    handleOpenDelete,
}: TEditMethodProps) => {
    const userAccountLink = getLocalStorageValue<SelfEmployedPayoutMethod>(
        'withdrawal-funds-account-link',
        EMPTY_PAYOUT_METHOD,
    )

    return (
        <BottomSheet open={isEditMode} onOpenChange={setIsEditMode}>
            <BottomSheetContent gripper showCloseButton={false}>
                <BottomSheetHeader className={styles['bottom-sheet__header']}>
                    <BottomSheetTitle>Удаление счёта</BottomSheetTitle>
                    <BottomSheetClose>
                        <button
                            className={styles['bottom-sheet__header-back']}
                            data-test-id='sbp-bottom-sheet__back'
                            type='button'
                        >
                            <IconBackArrowLeft color='var(--text-and-icon-primary)' size={24} />
                        </button>
                    </BottomSheetClose>
                </BottomSheetHeader>
                <BottomSheetBody className={styles['bottom-sheet__content']}>
                    <Box className={styles['delete-method']}>
                        <BankIcon
                            name={userAccountLink.bank_name}
                            url={userAccountLink.bank_icon_url ?? ''}
                        />
                        <Text className={styles['delete-method__text']} variant='body-m-16-medium'>
                            СБП • {userAccountLink.bank_name}
                        </Text>
                        <IconTrash
                            color='var(--text-and-icon-primary)'
                            size={24}
                            onClick={handleOpenDelete}
                        />
                    </Box>
                </BottomSheetBody>
            </BottomSheetContent>
        </BottomSheet>
    )
}

type TChangeBottomSheetProps = {
    bank: string
    handleChange: () => void
    isChangeMode: boolean
    phone: string
    setChangeMode: () => void
}

export const ChangeBottomSheet = ({
    bank,
    phone,
    isChangeMode,
    setChangeMode,
    handleChange,
}: TChangeBottomSheetProps) => {
    return (
        <BottomSheet open={isChangeMode} onOpenChange={setChangeMode}>
            <BottomSheetContent gripper showCloseButton={false}>
                <BottomSheetHeader className={styles['bottom-sheet__header']}>
                    <BottomSheetTitle>Сменить счёт СБП?</BottomSheetTitle>
                    <BottomSheetClose>
                        <button
                            className={styles['bottom-sheet__header-back']}
                            data-test-id='sbp-bottom-sheet__back'
                            type='button'
                        >
                            <IconBackArrowLeft color='var(--text-and-icon-primary)' size={24} />
                        </button>
                    </BottomSheetClose>
                </BottomSheetHeader>
                <BottomSheetBody className={styles['bottom-sheet__content']}>
                    <Text variant='body-m-16-medium'>
                        Ваши средства будут поступать на {bank} по номеру {formatPhone(phone)}
                    </Text>
                    <Box className={styles['delete-method__buttons']}>
                        <Button
                            style={{ flex: 1 }}
                            variant='secondary'
                            onClick={() => setChangeMode()}
                        >
                            Отмена
                        </Button>
                        <Button style={{ flex: 1 }} variant='primary' onClick={handleChange}>
                            Да
                        </Button>
                    </Box>
                </BottomSheetBody>
            </BottomSheetContent>
        </BottomSheet>
    )
}
